import React, { useState } from 'react';
import cn from 'classnames';
import { Popover } from 'antd';
import { useTheme } from 'admin/components/common/Theme';
import type { SortableItemState } from 'admin/components/common/SortableList';
import RowAction from 'admin/components/pages/Story/CardEditor/Inspector/PropField/RowAction';
import {
	AddMenuList,
	layerRowsCss as rows,
	moveLayer,
	type InheritanceType,
} from 'admin/components/pages/Story/CardEditor/Inspector/LayerRows';
import {
	getShadowLayerKind,
	isShadowCss,
	parseShadowLayers,
	PX_SCALE,
	stringifyShadowLayers,
	type ShadowEntry,
	type ShadowLayer,
} from 'admin/components/pages/Story/CardEditor/Inspector/BoxShadowSettings/shadow-layers';
import {
	getShadowAddItems,
	SHADOW_NAMES,
} from 'admin/components/pages/Story/CardEditor/Inspector/BoxShadowSettings/shadow-presets';
import {
	ShadowColorRow,
	ShadowLayerFields,
	ShadowLayerList,
	ShadowLayerRow,
	ShadowLayerValue,
} from 'admin/components/pages/Story/CardEditor/Inspector/BoxShadowSettings/ShadowLayerParts';
import { getDesignPresetTokenId, normalizeDesignPresetValue, type DesignPresetValue } from 'common/design-tokens';
import type { DesignPresetShadowProperty } from 'common/design-tokens/shadows';
import type { PresetCellSource } from './design-preset-cell';
import css from './DesignPresetsEditor.scss';

// Rows take the field colors of where the value comes from, as in the Inspector
const ROW_INHERITANCE: Record<PresetCellSource, InheritanceType> = {
	base: 'assigned',
	override: 'assigned',
	inherited: 'inherited',
	empty: 'default',
};

const errorMessage = (issue: unknown) => (issue instanceof Error ? issue.message : String(issue));

/** A sub-popover on the editor's surface: popovers render apart from the editor, so each takes the theme itself */
function Surface({ className, children }: { className: string; children: React.ReactNode }) {
	const { theme } = useTheme();
	return (
		<div className={cn(css.groupEditor, css.shadowSurface, className)} data-theme={theme}>
			{children}
		</div>
	);
}

/**
 * The Inspector's shadow rows and layer controls for a preset cell, in canonical px. Each edit is written as it is
 * made, as in the Inspector: a control writes when it is let go or loses focus. Escape closes a layer's controls,
 * then the editor.
 */
export function ShadowEditor({
	property,
	value,
	header,
	source,
	paint,
	renderColor,
	onChange,
	onClose,
}: {
	property: DesignPresetShadowProperty;
	value: DesignPresetValue;
	/** The property and the platform, as the group editors head themselves */
	header: React.ReactNode;
	source: PresetCellSource;
	/** A layer's color as painted: a linked color token resolves to its value */
	paint: (color: string) => string;
	/** A layer's color field, a preset field that links a color token as the Inspector's shadow color does */
	renderColor: (color: string, onChange: (color: string) => void) => React.ReactNode;
	onChange: (value: DesignPresetValue) => void;
	onClose: () => void;
}) {
	const { theme } = useTheme();
	const [editing, setEditing] = useState<number>();
	const [adding, setAdding] = useState(false);
	const [error, setError] = useState<string>();
	const layers = parseShadowLayers(String(value), property);
	const inheritance = ROW_INHERITANCE[source];
	const canDrag = layers.length > 1;
	const layerIds = layers.map((layer, index) => `${index}:${stringifyShadowLayers([layer], property)}`);

	const writeLayers = (next: ShadowEntry[]) => {
		try {
			onChange(normalizeDesignPresetValue(property, stringifyShadowLayers(next, property)));
			setError(undefined);
		} catch (issue) {
			setError(errorMessage(issue));
		}
	};
	const replaceLayer = (index: number, layer: ShadowEntry) => layers.map((item, i) => (i === index ? layer : item));
	const removeLayer = (index: number) => {
		setEditing(undefined);
		writeLayers(layers.filter((_, i) => i !== index));
	};
	const addItems = getShadowAddItems(property, (added, replaces) =>
		writeLayers(replaces ? added : [...added, ...layers])
	);

	const renderRow = (index: number, item: SortableItemState) => {
		const layer = layers[index];
		const row = {
			item,
			inheritance,
			canDrag,
			onRemove: () => removeLayer(index),
		};
		// Not a shadow the rows can show; presets only store ones they can, so it can only be removed
		if (isShadowCss(layer))
			return (
				<ShadowLayerRow {...row} label="CSS">
					<span className={cn(rows.value, css.shadowCssValue)}>{layer.css}</span>
				</ShadowLayerRow>
			);
		const edit = (edited: ShadowLayer) => writeLayers(replaceLayer(index, edited));
		return (
			<Popover
				trigger="click"
				placement="rightTop"
				arrow={false}
				open={editing === index}
				onOpenChange={open => setEditing(open ? index : undefined)}
				styles={{ container: { padding: 0 } }}
				content={
					<Surface className={css.shadowLayerPopover}>
						<ShadowLayerFields
							layer={layer}
							property={property}
							scale={PX_SCALE}
							onChange={edit}
							color={
								<ShadowColorRow>
									{renderColor(layer.color, color => edit({ ...layer, color }))}
								</ShadowColorRow>
							}
						/>
					</Surface>
				}
			>
				{/* The span takes the popover's ref; rows inside a list are drawn as blocks */}
				<span>
					<ShadowLayerRow {...row} label={getShadowLayerKind(layer)}>
						<ShadowLayerValue
							layer={layer}
							property={property}
							scale={PX_SCALE}
							fill={paint(layer.color) || 'currentColor'}
							token={getDesignPresetTokenId(layer.color) !== undefined}
						/>
					</ShadowLayerRow>
				</span>
			</Popover>
		);
	};

	return (
		<div
			className={cn(css.groupEditor, css.shadowSurface)}
			data-theme={theme}
			onKeyDown={event => {
				if (event.key !== 'Escape') return;
				event.stopPropagation();
				// A typed value is written when its field loses focus
				if (event.target instanceof HTMLElement) event.target.blur();
				if (editing !== undefined || adding) {
					setEditing(undefined);
					setAdding(false);
				} else onClose();
			}}
		>
			{header}
			<ShadowLayerList
				label={SHADOW_NAMES[property]}
				add={
					<Popover
						trigger="click"
						placement="bottomRight"
						arrow={false}
						open={adding}
						onOpenChange={setAdding}
						styles={{ container: { padding: 0 } }}
						content={
							<Surface className={css.shadowAddPopover}>
								<AddMenuList items={addItems} onClose={() => setAdding(false)} />
							</Surface>
						}
					>
						<RowAction
							action="add"
							label={`Add ${SHADOW_NAMES[property].toLowerCase()}`}
							className={rows.add}
						/>
					</Popover>
				}
				ids={layerIds}
				onMove={(from, to) => writeLayers(moveLayer(layers, from, to))}
				renderRow={renderRow}
			/>
			{error ? (
				<div className={css.fieldError} role="alert">
					{error}
				</div>
			) : null}
		</div>
	);
}
